import type { ClerkProviderProps } from '@clerk/clerk-react';
import { enUS, zhCN } from '@clerk/localizations';
import enCommon from './locales/en/common';
import zhCommon from './locales/zh/common';

type ClerkLocalization = NonNullable<ClerkProviderProps['localization']>;

// App resources use single braces; only the Clerk boundary emits its rich-link syntax.
const legalConsentLabel = (
  template: string,
  copy: typeof enCommon.auth.clerk,
): string => template
  .replace('{termsOfServiceLink}', `{{ termsOfServiceLink || link(${JSON.stringify(copy.termsOfService)}) }}`)
  .replace('{privacyPolicyLink}', `{{ privacyPolicyLink || link(${JSON.stringify(copy.privacyPolicy)}) }}`);

export const getClerkLocalization = (
  language: string,
  pathname: string,
): ClerkLocalization => {
  // The locale package brands interpolation strings differently from this React SDK.
  // Runtime resources are strings; keep the compatibility cast at this boundary only.
  const base = (language === 'zh-CN' ? zhCN : enUS) as unknown as ClerkLocalization;
  const copy = (language === 'zh-CN' ? zhCommon : enCommon).auth.clerk;
  const isSignUp = pathname === '/signup' || pathname.startsWith('/signup/');

  return {
    ...base,
    socialButtonsBlockButton: copy.googleButton,
    formFieldLabel__emailAddress: isSignUp
      ? copy.signUpEmailLabel
      : base.formFieldLabel__emailAddress,
    formFieldInputPlaceholder__emailAddress: copy.emailPlaceholder,
    formFieldInputPlaceholder__emailAddress_username: copy.emailOrUsernamePlaceholder,
    formFieldInputPlaceholder__password: copy.passwordPlaceholder,
    signIn: {
      ...base.signIn,
      start: {
        ...base.signIn?.start,
        title: copy.signInTitle,
        titleCombined: copy.signInTitle,
      },
    },
    signUp: {
      ...base.signUp,
      start: {
        ...base.signUp?.start,
        title: copy.signUpTitle,
        titleCombined: copy.signUpTitle,
        subtitle: '',
        subtitleCombined: '',
      },
      legalConsent: {
        ...base.signUp?.legalConsent,
        checkbox: {
          ...base.signUp?.legalConsent?.checkbox,
          label__onlyPrivacyPolicy: legalConsentLabel(copy.agreeToPrivacy, copy),
          label__onlyTermsOfService: legalConsentLabel(copy.agreeToTerms, copy),
          label__termsOfServiceAndPrivacyPolicy: legalConsentLabel(copy.agreeToTermsAndPrivacy, copy),
        },
        continue: {
          ...base.signUp?.legalConsent?.continue,
          title: copy.legalConsentTitle,
          subtitle: copy.legalConsentSubtitle,
        },
      },
    },
    organizationProfile: {
      ...base.organizationProfile,
      start: {
        ...base.organizationProfile?.start,
        profileSection: {
          ...base.organizationProfile?.start?.profileSection,
          uploadAction__title: 'Logo(optional)',
        },
      },
    },
    unstable__errors: {
      ...base.unstable__errors,
      not_allowed_access: copy.emailRequired,
      form_identifier_exists: copy.accountExists,
    },
  };
};
